import React, { Component } from 'react';
import { Helmet } from 'react-helmet';

// Layout
import Header from '../layout/header';
import Footer from '../layout/footer';

// Elements
import AvailableCitiesModal from '../elements/available-cities-modal';
import { startingPriceLabel } from '../../utils/pricing';

// Premium components
import {
  PremiumHero,
  SectionWrapper,
  FaqAccordion,
  FinalCTA,
  Breadcrumbs,
  StructuredData,
} from '../elements/premium';

// Comprehensive FAQ data
const GENERAL_FAQS = [
  { q: 'What is Ride N Repair?', a: 'Ride N Repair is a doorstep bike and car service platform. You can book online through our website or app, and a certified mechanic arrives at your location — home, office, or anywhere else — fully equipped to service or repair your vehicle.' },
  { q: 'How do I book a service?', a: 'You can book in under 60 seconds through our website or mobile app. Select your vehicle type, choose a service, pick a date and time slot, and enter your address. Our mechanic arrives at the scheduled time.' },
  { q: 'Which cities do you serve?', a: 'We currently serve 32+ cities across India including Delhi NCR (Noida, Gurgaon, Ghaziabad, Faridabad, Greater Noida), Bengaluru, Mumbai, Pune, Hyderabad, Chennai, Kolkata, Ahmedabad, Jaipur, Lucknow, Indore, Bhopal, Chandigarh, Dehradun, Surat, Nashik, Nagpur, Bhubaneswar, Guwahati, Mysore, Kochi, and more.' },
  { q: 'What brands and models do you service?', a: 'We service all major two-wheeler and four-wheeler brands — Honda, Hero, Bajaj, TVS, Royal Enfield, Yamaha, Suzuki, KTM, Kawasaki, Maruti Suzuki, Hyundai, Tata, Mahindra, Kia, Toyota, BMW, Mercedes-Benz, Audi, and 40+ more brands covering hundreds of models.' },
];

const SERVICE_FAQS = [
  { q: 'What services do you offer?', a: 'We provide a full range of services: periodic maintenance, engine repair, AC repair & gas refill, battery replacement & jump start, brake repair, clutch repair, suspension work, tyre replacement & puncture repair, denting & painting, electrical repairs, and emergency breakdown assistance — for both bikes and cars.' },
  { q: 'How long does a service take?', a: 'A standard bike service takes 60-120 minutes. Car services typically take 2-4 hours depending on the type. Complex repairs may take longer — your mechanic will give you an estimate before starting.' },
  { q: 'Do you use genuine parts?', a: 'Yes, we use only OEM and genuine spare parts for all repairs and replacements. We never compromise on quality — your safety depends on it.' },
  { q: 'Can I get my vehicle serviced at my office?', a: 'Absolutely! Our mechanics come to any location — home, office, parking lot, or wherever your vehicle is. Just ensure there\'s enough space for the mechanic to work safely.' },
  { q: 'What if my vehicle needs additional repairs discovered during service?', a: 'Your mechanic will inform you of any additional issues found, explain the problem with photos, and get your approval before proceeding. You\'re always in control — no surprise charges.' },
];

const PAYMENT_FAQS = [
  { q: 'How much does a service cost?', a: `Pricing is fully transparent — you see the exact cost before booking. Bike services start from ${startingPriceLabel('bike')} and car services from ${startingPriceLabel('car')}. Prices vary by vehicle type, model, and service selected. No hidden charges.` },
  { q: 'What payment methods do you accept?', a: 'We accept UPI (Google Pay, PhonePe, Paytm), credit/debit cards, net banking, and cash. Digital payments are recommended as they come with full service warranty.' },
  { q: 'How do I receive my bill?', a: 'A digital invoice is sent to your registered email immediately after service completion. You can also view it in the app under your booking history.' },
  { q: 'Can I cancel or reschedule my booking?', a: 'Yes, you can cancel or reschedule anytime. Cancelling is free until our mechanic reaches your location; once the mechanic has reached, a ₹399 visit charge applies, whether or not you go ahead with the service. Use the app or contact our support team at +91 120 361 5050.' },
];

const WARRANTY_FAQS = [
  { q: 'Do you provide a warranty on services?', a: 'Yes, every service comes with a 30-day warranty on parts and labour. If you face any issues related to the work performed, we\'ll fix it at no additional cost within the warranty period.' },
  { q: 'What does the warranty cover?', a: 'The warranty covers all work performed and parts replaced during the service. If any issue arises directly related to the service within 30 days, contact us and we\'ll resolve it free of charge.' },
  { q: 'How do I claim warranty?', a: 'Simply contact us through the app, call +91 120 361 5050, or email info@ridenrepair.com with your booking details. We\'ll arrange a follow-up visit at no cost.' },
];

class Faq extends Component {
  render() {
    const { isMobileBrowser, isMobileApp, user, setUser, citiesModal, setCitiesModal } = this.props;

    // Combined FAQs for schema
    const allFaqs = [...GENERAL_FAQS, ...SERVICE_FAQS, ...PAYMENT_FAQS, ...WARRANTY_FAQS];

    const schema = {
      '@type': 'FAQPage',
      mainEntity: allFaqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };

    return (
      <div className="rr-home rr-home--lux">
        <Helmet>
          <link rel="canonical" href="https://www.ridenrepair.com/faq" />
          <title>FAQ — Frequently Asked Questions | Ride N Repair</title>
          <meta name="description" content="Find answers to common questions about Ride N Repair's doorstep bike and car service — booking, pricing, warranty, payment, and more." />
          <meta property="og:title" content="FAQ — Ride N Repair" />
          <meta property="og:url" content="https://www.ridenrepair.com/faq" />
        </Helmet>

        <StructuredData data={schema} />

        {citiesModal && <AvailableCitiesModal closeModal={() => setCitiesModal(false)} />}

        <Header isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} user={user} setUser={setUser} />

        <PremiumHero
          compact
          badge="Help Center"
          title="Frequently Asked Questions"
          subtitle="Everything you need to know about Ride N Repair's doorstep vehicle service"
          isMobileBrowser={isMobileBrowser}
          ctas={[
            { label: 'Book a Service', href: '/service', variant: 'primary' },
            { label: 'Call: +91 120 361 5050', href: 'tel:+911203615050', variant: 'secondary' },
          ]}
        />

        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'FAQ' }]} />

        {/* GENERAL */}
        <SectionWrapper narrow kicker="General" heading="About Ride N Repair">
          <FaqAccordion faqs={GENERAL_FAQS} withSchema={false} />
        </SectionWrapper>

        {/* SERVICES */}
        <SectionWrapper narrow variant="gray" kicker="Services" heading="Service & Repair Questions">
          <FaqAccordion faqs={SERVICE_FAQS} withSchema={false} />
        </SectionWrapper>

        {/* PRICING & PAYMENT */}
        <SectionWrapper narrow kicker="Pricing" heading="Pricing & Payment">
          <FaqAccordion faqs={PAYMENT_FAQS} withSchema={false} />
        </SectionWrapper>

        {/* WARRANTY */}
        <SectionWrapper narrow variant="gray" kicker="Warranty" heading="Warranty & Support">
          <FaqAccordion faqs={WARRANTY_FAQS} withSchema={false} />
        </SectionWrapper>

        {/* CTA */}
        <FinalCTA
          heading={<>Still Have Questions?<br />We're Here to Help.</>}
          subtitle="Call us at +91 120 361 5050 or email info@ridenrepair.com"
          ctas={[
            { label: 'Book Now', href: '/service', variant: 'primary' },
            { label: 'Contact Us', href: '/contact', variant: 'white-outline' },
          ]}
        />

        <Footer isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} setCitiesModal={setCitiesModal} />
      </div>
    );
  }
}

export default Faq;
